import { isFunction } from '@dimjs/lang';
import { get, getCtx } from '@dimjs/utils';
import { utils } from '../../utils/index.js';
import { callbackIdFactory } from '../callback-factory.js';
import { type WebviewJavascriptBridgeApi } from '../type.api.js';
import { type CallNtvData, type FabricCallbackData } from '../type.builtin.js';
import { type AndroidSendWebCallbackDataToNtv } from './types.android.js';

const { log, parse } = utils;
const callbackIndexes = callbackIdFactory('android');

/**
 * Try to resolve callback synchronous
 * @param callbackId 回调ID
 * @param responseData 回调的数据.
 */
const tryResolveSyncCallback = (
  callbackId: string,
  responseData: string
): FabricCallbackData => {
  if (!callbackId) {
    // 不需要callback, 同步函数调用(同步回调结果或者空对象)
    return parse.jsonParse<FabricCallbackData>(
      responseData || '{"code":"0","data":{}}'
    );
  }
  // 即便是异步接口, 如果Native 直接返回了
  // 我们也认为是Native同步执行结果返回, 直接清理内存 删除callback函数引用
  if (typeof responseData !== 'undefined') {
    const callback = window[callbackId];
    if (!callback) {
      return { code: '0', data: null };
    }
    delete window[callbackId];
    callback(
      parse.jsonParse<FabricCallbackData>(
        responseData || '{"code":"0","data":{}}'
      )
    );
    return { code: '0', data: null };
  }
  return { code: '0', data: null };
};
/**
 * 内部方法WEB向Native发送消息.
 * @param method 方法名
 * @param options 参数数据
 * @param callback 回调字符串/函数回调, 字符串为Native主动发起, WEB回调Ntv的时候就为字符串
 * @param complete Note这里如果为`undefined`, 则不需要传递到native `complete`参数. 所有主动发起的方法都是不需要complete概念的
 */
const _call = <TMethod extends string>(
  method: TMethod,
  options?: CallNtvData | Function,
  callback?: Function | string,
  complete?: boolean
): FabricCallbackData => {
  if (typeof options === 'function') {
    callback = options;
    options = {};
  }
  const callData = options === undefined ? null : options;

  let callbackId = '';
  if (typeof callback === 'string') {
    callbackId = callback;
  } else if (typeof callback === 'function') {
    callbackId = callbackIndexes();
    // 直接挂载到windows 这里主要是为了与ios保持一致. 同时方便`call()`
    // 调用的回调函数, 直接的变化, check内存是否有被回收.
    window[callbackId] = callback;
  }
  // 针对 android 方法定义永远接收2个参数, [入参, 回调ID], 如果是WEB回调处理结果内容给Native则需要传递complete标识
  const callArgs: unknown[] = [JSON.stringify(callData), callbackId];
  // 如果 `complete` 不传递, 则认为是主动调用Native 注册好的方法, 没有complete的概念.
  if (typeof complete !== 'undefined') {
    callArgs.push(complete);
  }
  log.logData(`[Android] Call Ntv(${method})输入参数:`, callArgs);

  const methodName = method as string;
  const ntvPluginHandler =
    window[methodName] || get(window as object, methodName);

  try {
    if (isFunction(ntvPluginHandler)) {
      const ctx = window[methodName]
        ? window
        : getCtx(window as object, methodName);
      const responseData = ntvPluginHandler.call(
        ctx,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ...callArgs
      ) as unknown as string;
      log.logData(
        `[Android] Call Ntv(${methodName})同步返回数据内容串:`,
        responseData
      );
      // Return resolve sync callback result
      return tryResolveSyncCallback(callbackId, responseData);
    } else {
      log.logWarn(
        `[Android] Call Ntv 无命名空间注解方法 ("${methodName}")存在, 尝试通过send()方法代理调用!`
      );
      // 保持4个参数, 当Native直接在主线层注册一个函数让web调用, 并非通过@JavascriptInterface()生成到window命名空间下.
      // 这个时候是不会在window.fabric.xx生成这个函数签名, 只能通过send()方法间接调用
      // (methodName, data, callbackId)
      const responseData = window['fabric'].send(...[methodName, ...callArgs]);
      // Return resolve sync callback result
      return tryResolveSyncCallback(callbackId, responseData);
    }
  } catch (err: any) {
    log.logError(
      '[Android] WebViewJavascriptBridge: WARNING: javascript call() handler threw.',
      err
    );
    return tryResolveSyncCallback(
      callbackId,
      JSON.stringify({
        code: 'fabric_catched_error',
        data: null,
        message: err?.message,
      })
    );
  }
};

/**
 * WEB主动调用Native接口.
 * @param method 方法名
 * @param options 参数数据
 * @param callback 回调函数
 */
export const call: WebviewJavascriptBridgeApi['call'] = <
  TMethod extends string,
>(
  method: TMethod,
  options?: CallNtvData | Function,
  callback?: Function | string
): FabricCallbackData => {
  return _call(method, options, callback);
};

/**
 * 当Native主动调用Web的时候,Web端处理完, 回调给结果数据到Native的通道
 * 因为android 默认内置命名空间为`fabric`, 这里直接调用`fabric.response`
 */
export const webCallbackToNtv = (
  callbackData: AndroidSendWebCallbackDataToNtv
) => {
  const { callbackId, data, complete } = callbackData;
  log.logData('[Android] webCallbackToNtv()', 'callbackData:', callbackData);
  // 因为Native调用客户端只能通过webview实例上的`callhandler()`在主线程调用. 所有使用`plugincore`里面的默认
  // 命名空间(`fabric`)即可, 不需要指定特定的插件命名空间(`fabric_http.response`)
  _call(`fabric.response`, data, callbackId, complete);
};
